import LoginBackground from '../../resources/bitmapteste.png';
import logo from '../../resources/logo.png';
import RegisterService from '../../rest/registerService';
import * as Yup from 'yup';
import { useState } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { validateCPF } from '../../utils/DocumentUtils';
import FormInput from '../../components/lib/inputField';
import FormSection from '../../components/lib/formSection';
import FormSelect from '../../components/lib/formSelect';
import CustomButton from '../../components/lib/customButton';
import { MdOutlineCancel } from 'react-icons/md';

export default function CadastroStepper() {
    const MAX_RESPONSAVEIS = 2;
    const MAX_ALUNOS = 5;

    const validationSchema = Yup.object({
        responsaveis: Yup.array()
            .of(
                Yup.object({
                    nome: Yup.string().required('Nome é obrigatório'),
                    cpf: Yup.string()
                        .matches(/^\d{11}$/, 'CPF deve conter 11 números sem pontos ou traços')
                        .required('CPF é obrigatório')
                        .test('valid-cpf', 'CPF inválido.', value => {
                            if (!value) return false;
                            return validateCPF(value);
                        }),
                    email: Yup.string().email('Email inválido').required('Email é obrigatório'),
                    telefone: Yup.string().required('Telefone é obrigatório'),
                })
            )
            .min(1, 'Deve ter pelo menos um responsável')
            .max(2, 'Máximo de 2 responsáveis'),
        alunos: Yup.array()
            .of(
                Yup.object({
                    nome: Yup.string().required('Nome é obrigatório'),
                    ra: Yup.string().required('R.A é obrigatório'),
                    email: Yup.string().email('Email inválido'),
                    telefone: Yup.string(),
                    serie: Yup.string().required('Série é obrigatória'),
                    limiteEmergencia: Yup.number().required('Limite de Emergência é obrigatório'),
                    limiteGastos: Yup.number().required('Limite de Gastos Diários é obrigatório')
                })
            )
            .max(MAX_ALUNOS, `Máximo de ${MAX_ALUNOS} alunos`),
    });

    const formik = useFormik({
        initialValues: {
            responsaveis: [
                { nome: '', cpf: '', email: '', telefone: '', endereco: '' }
            ],
            alunos: [
                { nome: '', ra: '', email: '', telefone: '', serie: '', limiteEmergencia: 0, limiteGastos: 0 }
            ],
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            if (step === 2) {
                const path = window.location.pathname;
                const code = path.split('/cadastrar/')[1]?.split('/preencherFormulario')[0];

                try {
                    await RegisterService.registerCustomers({
                        parents: values.responsaveis.map(r => ({
                            name: r.nome,
                            cpf: r.cpf,
                            email: r.email,
                            phone_number: r.telefone,
                            address: r.endereco,
                            client_code: code,
                        })),
                        students: values.alunos.map(a => ({
                            name: a.nome,
                            login: a.ra,
                            email: a.email,
                            phone_number: a.telefone,
                            grade: a.serie,
                            emergency_value: a.limiteEmergencia,
                            spend_limit: a.limiteGastos,
                            client_code: code,
                        }))
                    }, code);
                } catch (error) {
                    console.error('Erro no cadastro:', error);

                    if (error.response && error.response.status === 409) {
                        const message = error.response.data.message;

                        const formikErrors = {};
                        const formikTouched = {};

                        values.alunos.forEach((aluno, index) => {
                            if (message.includes('login') && message.includes(aluno.ra)) {
                                formikErrors[`alunos[${index}].ra`] = 'O R.A informado já está em uso.';
                                formikTouched[`alunos[${index}].ra`] = true;
                            }

                            if (message.includes('email') && message.includes(aluno.email)) {
                                formikErrors[`alunos[${index}].email`] = 'O email informado já está em uso.';
                                formikTouched[`alunos[${index}].email`] = true;
                            }
                        });

                        formik.setErrors(formikErrors);
                        formik.setTouched(formikTouched);
                    }
                }
            }
            else {
                formik.validateForm().then(errors => {
                    if (Object.keys(errors).length === 0) {
                        setStep(step + 1);
                    } else {
                        formik.setTouched(
                            Object.keys(errors).reduce((acc, key) => {
                                acc[key] = true;
                                return acc;
                            }, {})
                        );
                    }
                });
            }
        }
    });

    const handleCpfBlur = (index) => (e) => {
        const digits = e.target.value.replace(/\D/g, '');
        if (digits.length > 0 && digits.length < 11) {
            formik.setFieldValue(`responsaveis[${index}].cpf`, digits.padStart(11, '0'));
        }
        formik.handleBlur(e);
    };

    const handleRemoveResponsavel = (indexToRemove) => {
        const novosResponsaveis = formik.values.responsaveis.filter((_, index) => index !== indexToRemove);
        formik.setFieldValue("responsaveis", JSON.parse(JSON.stringify(novosResponsaveis)));
    };

    const handleRemoveAluno = (indexToRemove) => {
        const novosAlunos = formik.values.alunos.filter((_, index) => index !== indexToRemove);
        formik.setFieldValue("alunos", JSON.parse(JSON.stringify(novosAlunos)));
    };

    const hasResponsaveisErrors = Boolean(formik.errors.responsaveis);
    const hasAlunosErrors = Boolean(formik.errors.alunos);

    const [step, setStep] = useState(0);
    const handleNextStep = () => setStep((s) => s + 1);
    const handlePreviousStep = () => setStep((s) => s - 1);

    return (
        <div className='relative h-screen w-full flex items-center justify-center' >
            <img src={LoginBackground} alt='login screen background' className='absolute inset-0 object-cover w-full h-full' />

            <div className='flex items-center justify-center w-full h-4/5'>
                <div className='w-3/4 max-w-5xl p-8 bg-white rounded-2xl shadow-lg z-10 border-2 border-slate-200 space-y-4'>
                    <img src={logo} alt='Logo Foodz' className='w-1/4 mx-auto' />
                    <FormikProvider value={formik}>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                formik.handleSubmit(e);
                            }}
                        >
                            {step === 0 && (
                                <div className='max-h-120 overflow-y-auto px-2'>
                                    {
                                        formik.values.responsaveis.map((_, index) => (
                                            <div key={index} className="relative border border-1 rounded p-6 mb-6">
                                                <FormInput label="Nome" name={`responsaveis[${index}].nome`} />
                                                <FormSection columns={2} verticalMargin={2}>
                                                    <FormInput label="CPF" name={`responsaveis[${index}].cpf`} onBlur={handleCpfBlur(index)} />
                                                    <FormInput label="E-mail" name={`responsaveis[${index}].email`} />
                                                </FormSection>
                                                <FormSection columns={2} verticalMargin={2}>
                                                    <FormInput label="Telefone" name={`responsaveis[${index}].telefone`} />
                                                    <FormInput label="Endereço" name={`responsaveis[${index}].endereco`} required={false} />
                                                </FormSection>
                                                {formik.values.responsaveis.length > 1 && (
                                                    <MdOutlineCancel
                                                        size={30}
                                                        onClick={() => handleRemoveResponsavel(index)}
                                                        className="absolute top-0 right-0 p-1 text-red-500 rounded-full cursor-pointer"
                                                    />
                                                )}
                                            </div>
                                        ))
                                    }
                                    <div className="flex justify-center">
                                        <CustomButton
                                            type={'button'}
                                            buttonType='confirm'
                                            showIcon={false}
                                            text={'Adicionar Responsável'}
                                            disabled={formik.values.responsaveis.length >= 2}
                                            onClick={() => {
                                                const current = formik.values.responsaveis;
                                                if (current.length < MAX_RESPONSAVEIS) {
                                                    formik.setFieldValue('responsaveis', [
                                                        ...current,
                                                        { nome: '', cpf: '', email: '', telefone: '', endereco: '' }
                                                    ]);
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className='flex justify-end mt-4'>
                                        <CustomButton type={'button'} text='Avançar' buttonType={"forwardSmall"} onClick={handleNextStep} disabled={hasResponsaveisErrors} />
                                    </div>
                                </div>
                            )}

                            {step === 1 && (
                                <div className='max-h-128 overflow-y-auto px-2'>
                                    {formik.values.alunos.map((_, index) => (
                                        <div key={index} className="relative border border-1 rounded p-6 mb-6">
                                            <FormInput label="Nome" name={`alunos[${index}].nome`} />
                                            <FormSection columns={2} verticalMargin={2}>
                                                <FormSelect
                                                    label="Série"
                                                    key={`serie-${index}`}
                                                    name={`alunos[${index}].serie`}
                                                    options={[
                                                        "Infantil I",
                                                        "Infantil II",
                                                        "1º E.F",
                                                        "2º E.F",
                                                        "3° E.F",
                                                        "4º E.F",
                                                        "5° E.F",
                                                        "6° E.F",
                                                        "7° E.F",
                                                        "8° E.F",
                                                        "9° E.F",
                                                        "1° E.M",
                                                        "2° E.M",
                                                        "3° E.M"
                                                    ]}
                                                />
                                                <FormInput label="R.A" name={`alunos[${index}].ra`} />
                                            </FormSection>
                                            <FormSection columns={2} verticalMargin={2}>
                                                <FormInput label="E-mail" name={`alunos[${index}].email`} required={false} />
                                                <FormInput label="Telefone" name={`alunos[${index}].telefone`} required={false} />
                                            </FormSection>
                                            <FormSection columns={2} verticalMargin={2}>
                                                <FormInput label="Valor de Emergência (R$)" name={`alunos[${index}].limiteEmergencia`} type="number" step="0.5" min="1.0" />
                                                <FormInput label="Limite de gastos diário (R$)" name={`alunos[${index}].limiteGastos`} type="number" step="0.5" min="1.0" />
                                            </FormSection>
                                            {formik.values.alunos.length > 1 && (
                                                <MdOutlineCancel
                                                    size={30}
                                                    onClick={() => handleRemoveAluno(index)}
                                                    className="absolute top-0 right-0 p-1 text-red-500 rounded-full cursor-pointer"
                                                />
                                            )}
                                        </div>
                                    ))}
                                    <div className="flex justify-center mb-4">
                                        <CustomButton
                                            buttonType='confirm'
                                            showIcon={false}
                                            type={'button'}
                                            text={'Adicionar Aluno'}
                                            disabled={formik.values.alunos.length >= 5}
                                            onClick={() => {
                                                const current = formik.values.alunos;

                                                if (current.length < MAX_ALUNOS) {
                                                    formik.setFieldValue('alunos', [
                                                        ...current,
                                                        {
                                                            nome: '', ra: '', email: '', telefone: '', serie: '', limiteEmergencia: '', limiteGastos: '',
                                                        }
                                                    ]);
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className='flex justify-between mt-4'>
                                        <CustomButton type={'button'} buttonType={'backSmall'} text={"Voltar"} onClick={handlePreviousStep} />
                                        <CustomButton type={'submit'} showIcon={false} buttonType={'confirm'} text={"Enviar"} disabled={hasAlunosErrors} />
                                    </div>
                                </div>)}
                        </form>
                    </FormikProvider>
                </div >
            </div >
        </div >
    );
}
